"use client";

import { useGamjaExamCompletion } from "@/hooks/useGamjaExamCompletion";
import { cn } from "@/lib/utils";

/**
 * 감자시험 도전 횟수 표시 (JCG-3865).
 *
 * 감자시험 세트의 모든 문제를 채점한 횟수를 "도전"으로 보여준다. 기출 완주 뱃지(RoundCompletionBadge)와
 * 같이 Premium 이상에서만 표시하며, 비프리미엄·비로그인·로딩 중에는 아무것도 렌더하지 않는다.
 * 색은 비-amber 단색 배지 규칙(ui-patterns/dark-mode-colors.md)에 따라 orange-700/흰 글씨 ↔ orange-400/950.
 */

/** 감자시험 카드 우상단 뱃지. 도전 기록이 있을 때만 표시한다(미도전 표시는 하지 않음). */
export function GamjaExamChallengeBadge({
  href,
  compact = false,
  className,
}: {
  href: string;
  /**
   * 좁은 타일(홈 사이드바)은 항상 🔥+숫자만 보여준다. 기본은 모바일 2열 카드에서 이미지와 겹치지 않게
   * 🔥+숫자로 줄이고, sm 이상에서 "3번 도전"까지 보여준다. 스크린리더는 항상 "3번 도전"을 읽는다.
   */
  compact?: boolean;
  className?: string;
}) {
  const { showCompletion, completionForHref } = useGamjaExamCompletion();
  const count = completionForHref(href);
  if (!showCompletion || count === 0) return null;

  return (
    <span
      className={cn(
        "pointer-events-none inline-flex h-5 min-w-5 items-center justify-center gap-0.5 whitespace-nowrap rounded-full bg-orange-700 px-1 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-background dark:bg-orange-400 dark:text-orange-950",
        !compact && "sm:h-6 sm:px-2 sm:text-[11px]",
        className,
      )}
    >
      <span aria-hidden="true" className="text-[9px]">
        🔥
      </span>
      {/* 숫자와 "번 도전"을 한 flex 항목으로 묶어 gap 이 끼지 않게 한다. */}
      <span>
        <span className="tabular-nums">{count}</span>
        <span className={compact ? "sr-only" : "sr-only sm:not-sr-only"}>번 도전</span>
      </span>
    </span>
  );
}

/** 감자시험 페이지 문제 위: 지금까지의 도전 횟수 */
export function GamjaExamChallengeSummary({ slug }: { slug: string }) {
  const { showCompletion, completionForHref } = useGamjaExamCompletion();
  if (!showCompletion) return null;
  const count = completionForHref(slug);

  if (count === 0) {
    return (
      <p className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
        <span aria-hidden="true">🔥</span>
        <span>모든 문제를 채점하면 도전 횟수가 기록돼요</span>
      </p>
    );
  }

  return (
    <p className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-orange-300 bg-orange-50 px-3 py-1 text-sm font-semibold text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300">
      <span aria-hidden="true">🔥</span>
      <span>
        지금까지 <span className="tabular-nums">{count}</span>번 도전했어요
      </span>
    </p>
  );
}

/**
 * 감자시험 페이지 "다시 풀기" 위: 방금 세트를 끝냈을 때의 완료 안내.
 * 마지막 문제를 채점한 위치(페이지 하단)에서 바로 보이도록 둔다. live region 은 먼저 마운트해 둔다.
 */
export function GamjaExamChallengeResult({
  completedCount,
}: {
  /** 이번 세트로 기록된 도전 횟수. 아직 세트를 끝내지 않았으면 null */
  completedCount: number | null;
}) {
  const { showCompletion } = useGamjaExamCompletion();
  if (!showCompletion) return null;

  return (
    <div role="status" aria-live="polite" className="w-full max-w-sm">
      {completedCount !== null && (
        <div className="rounded-lg border border-orange-300 bg-orange-50 px-4 py-3 text-center dark:border-orange-800 dark:bg-orange-950">
          <p className="text-base font-bold text-orange-700 dark:text-orange-300">
            <span aria-hidden="true">🔥 </span>
            <span className="tabular-nums">{completedCount}</span>번째 도전 완료!
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            다시 풀기로 새 문제에 도전해 보세요.
          </p>
        </div>
      )}
    </div>
  );
}
